import {
  Input,
  Modal,
  Space,
  Typography,
  Upload,
  DatePicker,
  Checkbox,
  Alert,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { Form, Formik } from "formik";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import {
  createVacancy,
  getVacancyList,
  updateVacancyById,
} from "../../store/slices/vacancySlice";
import moment from "moment";

const { Title, Text } = Typography;

const AddVacancyPopup = ({ closePopup, item, onUpdateSuccess }) => {
  const formikRef = useRef(null);
  const handleCancel = () => {
    closePopup(false);
  };
  const dispatch = useDispatch();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const initialValues = {
    title: item?.title ?? "",
    description: item?.description ?? "",
    deadline: item?.deadline ? moment(item?.deadline) : null,
    firstPublishedDate: item?.firstPublishedDate
      ? moment(item?.firstPublishedDate)
      : null,
    applyUrl: item?.applyUrl ?? "",
    file: item?.filePath ?? null,
    isPublished: item?.isPublished || false,

    // location: item?.location ?? "",
    // note: item?.note ?? "",
    // requiredDocument: item?.requiredDocument ?? "",
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    deadline: Yup.date().required("Deadline is required"),
    firstPublishedDate: Yup.date().required("First published date is required"),
    applyUrl: Yup.string(),
    file: Yup.mixed().required("File is required"),
    isPublished: Yup.boolean(),

    // location: Yup.string(),
    // note: Yup.string(),
    // requiredDocument: Yup.string(),
  });

  const onSubmit = (values) => {
    setConfirmLoading(true);

    if (item) {
      const data = {
        title: values.title,
        description: values.description,
        deadline: values.deadline,
        firstPublishedDate: values.firstPublishedDate,
        applyUrl: values.applyUrl,
        isPublished: values.isPublished,
        id: item.id,
      };

      dispatch(
        updateVacancyById(item.id, data, () => {
          setConfirmLoading(false);
          closePopup(false);
          onUpdateSuccess && onUpdateSuccess();
        })
      );
    } else {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("deadline", values.deadline);
      formData.append("firstPublishedDate", values.firstPublishedDate);
      formData.append("applyUrl", values.applyUrl);
      formData.append("file", values.file);
      formData.append("isPublished", values.isPublished);
      // formData.append("location", values.location);
      // formData.append("note", values.note);
      // formData.append("requiredDocument", values.requiredDocument);

      dispatch(
        createVacancy(formData, () => {
          setConfirmLoading(false);
          closePopup(false);
          dispatch(getVacancyList());
        })
      );
    }
  };

  return (
    <>
      <Modal
        title={item ? "Edit Vacancy" : "Add Vacancy"}
        open={true}
        confirmLoading={confirmLoading}
        onOk={() => {
          formikRef.current.submitForm();
        }}
        width={768}
        onCancel={handleCancel}
      >
        <>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleChange, values, setFieldValue }) => {
              return (
                <Form>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size={20}
                  >
                    <div>
                      <Title level={5}>Title *</Title>
                      <Input
                        placeholder="Enter title"
                        name="title"
                        value={values.title}
                        onChange={handleChange}
                      />
                      {errors.title && touched.title ? (
                        <Text type="danger">{errors.title}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Description *</Title>
                      <Input.TextArea
                        placeholder="Enter description"
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                      />
                      {errors.description && touched.description ? (
                        <Text type="danger">{errors.description}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>First Published Date *</Title>
                      <DatePicker
                        style={{ width: "100%" }}
                        format="MM/DD/YYYY"
                        value={values.firstPublishedDate}
                        placeholder="Select date"
                        onChange={(value) =>
                          setFieldValue("firstPublishedDate", value)
                        }
                        // disabledDate={(current) => {
                        //   return moment().add(0, "days") < current;
                        // }}
                      />
                      {errors.firstPublishedDate &&
                      touched.firstPublishedDate ? (
                        <Text type="danger">{errors.firstPublishedDate}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Deadline *</Title>
                      <DatePicker
                        style={{ width: "100%" }}
                        format="MM/DD/YYYY"
                        value={values.deadline}
                        placeholder="Select date"
                        onChange={(value) => setFieldValue("deadline", value)}
                        disabledDate={(current) => {
                          return moment().add(0, "days") > current;
                        }}
                      />
                      {errors.deadline && touched.deadline ? (
                        <Text type="danger">{errors.deadline}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Application Form Link *</Title>
                      <Input
                        placeholder="Enter application form link"
                        name="applyUrl"
                        value={values.applyUrl}
                        onChange={handleChange}
                      />
                      {errors.applyUrl && touched.applyUrl ? (
                        <Text type="danger">{errors.applyUrl}</Text>
                      ) : null}
                    </div>

                    {!item && (
                      <div>
                        <Title level={5}>Upload File *</Title>
                        <Alert
                          message="Recommended file size: max 2MB."
                          type="warning"
                          style={{ marginTop: "4px", marginBottom: "16px" }}
                        ></Alert>
                        <Upload.Dragger
                          name="file"
                          action={"/"}
                          multiple={false}
                          fileList={values.file ? [values.file] : []}
                          onChange={(value) =>
                            setFieldValue("file", value.file.originFileObj)
                          }
                          accept=".pdf"
                        >
                          <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">Support for a .pdf</p>
                        </Upload.Dragger>
                        {errors.file && touched.file ? (
                          <Text type="danger">{errors.file}</Text>
                        ) : null}
                      </div>
                    )}

                    <Space>
                      <Checkbox
                        name="isPublished"
                        checked={values.isPublished}
                        onChange={(e) =>
                          setFieldValue("isPublished", e.target.checked)
                        }
                      >
                        Publish on website
                      </Checkbox>
                    </Space>

                    {/* <div>
                      <Title level={5}>Location *</Title>
                      <Input.TextArea
                        placeholder="Enter location"
                        name="location"
                        value={values.location}
                        onChange={handleChange}
                      />
                      {errors.location && touched.location ? (
                        <Text type="danger">{errors.location}</Text>
                      ) : null}
                    </div>

                    <div>
                      <Title level={5}>Note *</Title>
                      <Input.TextArea
                        placeholder="Enter note"
                        name="note"
                        value={values.note}
                        onChange={handleChange}
                      />
                      {errors.note && touched.note ? (
                        <Text type="danger">{errors.note}</Text>
                      ) : null}
                    </div> */}

                    {/* <div>
                      <Title level={5}>Required Documents *</Title>
                      <Input.TextArea
                        placeholder="Enter required documents"
                        name="requiredDocument"
                        value={values.requiredDocument}
                        onChange={handleChange}
                      />
                      {errors.requiredDocument && touched.requiredDocument ? (
                        <Text type="danger">{errors.requiredDocument}</Text>
                      ) : null}
                    </div> */}
                  </Space>
                </Form>
              );
            }}
          </Formik>
        </>
      </Modal>
    </>
  );
};

export default AddVacancyPopup;
